<!--
* @Author: AI Assistant
* @Date: 2026-01-12
* @Description: API History 页面 - 任务历史记录
-->
<template>
    <div class="history-page-container p-[14px] md:p-8 max-w-[1360px] mx-auto">
        <h1 class="sr-only">{{ t('api.historyTitle') }}</h1>
        <!-- 页面标题和搜索 -->
        <div
            class="flex flex-col md:flex-row md:items-end md:justify-between gap-[21px] md:gap-6 mb-[21px] md:mb-8"
        >
            <!-- 标题区域 -->
            <div class="flex flex-col gap-[1.75px] md:gap-1">
                <h2
                    class="text-[17.5px] md:text-2xl font-semibold text-white leading-[1.4]"
                >
                    {{ t('api.historyTitle') }}
                </h2>
                <p class="text-[11px] md:text-sm text-[#A1A1AA] leading-[1.5]">
                    {{ t('api.historyDesc') }}
                </p>
            </div>
            <!-- 搜索和筛选区域 -->
            <div class="flex flex-row gap-2 md:gap-3 w-full md:w-auto shrink-0">
                <!-- 搜索框 -->
                <div class="relative flex-1 md:flex-initial md:w-[216px]">
                    <svg
                        class="absolute left-[10px] top-1/2 -translate-y-1/2 text-zinc-500 w-3.5 h-3.5 md:w-4 md:h-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="m21 21-4.34-4.34" />
                        <circle cx="11" cy="11" r="8" />
                    </svg>
                    <input
                        v-model="searchQuery"
                        type="text"
                        :placeholder="t('api.searchId')"
                        class="w-full h-10 pl-9 pr-[10px] py-[7px] bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[7px] text-[11px] md:text-sm text-[#A1A1AA] placeholder:text-[#A1A1AA] outline-none"
                        @keyup.enter="handleSearch"
                    />
                </div>
                <!-- 筛选按钮 -->
                <div ref="filterRef" class="relative shrink-0 md:shrink-0">
                    <button
                        class="flex items-center justify-center gap-[6px] h-10 px-[10px] py-[7px] bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[7px] text-[11px] md:text-sm text-[#A1A1AA]"
                        @click.stop="toggleFilter"
                    >
                        <svg
                            class="w-3.5 h-3.5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path
                                d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"
                            />
                        </svg>
                        <span>{{ t('api.filter') }}</span>
                    </button>
                    <!-- 筛选下拉菜单 -->
                    <div
                        v-if="showFilter"
                        class="absolute top-full right-0 p-[4px] pb-0 mt-2 w-[140px] bg-[#1C1C20] border border-white/10 rounded-[10px] shadow-[0px_20px_60px_0px_rgba(0,0,0,0.5)] z-50 overflow-hidden animate-fadeIn"
                        @click.stop
                    >
                        <button
                            v-for="status in statusOptions"
                            :key="status.value"
                            class="w-full text-left px-4 py-3 mb-[4px] rounded-[6px] text-[11px] md:text-sm text-white hover:bg-white/10 transition-colors relative"
                            :class="{
                                'bg-white/10': filterStatus === status.value,
                            }"
                            @click="selectStatus(status.value)"
                        >
                            {{ status.label }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 历史记录表格 -->
        <div
            class="bg-[rgba(24,24,27,0.5)] border border-zinc-800 rounded-[10.5px] overflow-hidden shadow-lg"
        >
            <div class="overflow-x-auto">
                <table class="w-full text-left whitespace-nowrap min-w-[720px]">
                    <thead
                        class="bg-black/50 border-b border-zinc-800 text-zinc-500 text-[10px] md:text-xs font-semibold uppercase"
                    >
                        <tr>
                            <th class="px-3 md:px-6 py-3 md:py-4">{{ t('api.time') }}</th>
                            <th class="px-3 md:px-6 py-3 md:py-4">
                                {{ t('api.requestId') }}
                            </th>
                            <th class="px-3 md:px-6 py-3 md:py-4">TASK ID</th>
                            <th class="px-3 md:px-6 py-3 md:py-4">{{ t('api.method') }}</th>
                            <th class="px-3 md:px-6 py-3 md:py-4">{{ t('api.operationType') }}</th>
                            <th class="px-3 md:px-6 py-3 md:py-4">{{ t('api.model') }}</th>
                            <th class="px-3 md:px-6 py-3 md:py-4">{{ t('api.status') }}</th>
                            <th class="px-3 md:px-6 py-3 md:py-4">{{ t('api.duration') }}</th>
                            <th class="px-3 md:px-6 py-3 md:py-4 text-right">
                                {{ t('api.costCredits') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-zinc-800">
                        <!-- 骨架屏加载状态 -->
                        <template v-if="loading">
                            <tr
                                v-for="i in pageSize"
                                :key="`skeleton-${i}`"
                                class="text-[10px] md:text-sm"
                            >
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-3 md:h-4 w-16 md:w-24 rounded"
                                    ></div>
                                </td>
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-3 md:h-4 w-20 md:w-32 rounded"
                                    ></div>
                                </td>
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-3 md:h-4 w-20 md:w-32 rounded"
                                    ></div>
                                </td>
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-4 md:h-5 w-10 md:w-14 rounded"
                                    ></div>
                                </td>
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-3 md:h-4 w-16 md:w-24 rounded"
                                    ></div>
                                </td>
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-3 md:h-4 w-20 md:w-28 rounded"
                                    ></div>
                                </td>
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-5 md:h-6 w-12 md:w-16 rounded-full"
                                    ></div>
                                </td>
                                <td class="px-3 md:px-6 py-3 md:py-4">
                                    <div
                                        class="skeleton-shimmer h-3 md:h-4 w-14 md:w-20 rounded"
                                    ></div>
                                </td>
                                <td
                                    class="px-3 md:px-6 py-3 md:py-4 text-right"
                                >
                                    <div
                                        class="skeleton-shimmer h-3 md:h-4 w-8 md:w-12 rounded ml-auto"
                                    ></div>
                                </td>
                            </tr>
                        </template>

                        <!-- 真实数据列表 -->
                        <tr
                            v-else
                            v-for="record in filteredRecords"
                            :key="record.id"
                            class="hover:bg-white/5 transition-colors text-[10px] md:text-sm"
                        >
                            <td class="px-3 md:px-6 py-3 md:py-4 text-zinc-500">
                                {{ record.created_at }}
                            </td>
                            <td
                                class="px-3 md:px-6 py-3 md:py-4 font-mono text-zinc-300"
                            >
                                {{ record.id }}
                            </td>
                            <td
                                class="px-3 md:px-6 py-3 md:py-4 font-mono text-zinc-300"
                            >
                                {{ record.task_id }}
                            </td>
                            <td class="px-3 md:px-6 py-3 md:py-4">
                                <span
                                    class="px-1.5 md:px-2 py-0.5 rounded text-[9px] md:text-[10px] font-bold border"
                                    :class="
                                        record.method === 'POST'
                                            ? 'bg-lime-400/10 text-lime-400 border-lime-400/20'
                                            : 'bg-blue-900/20 text-blue-400 border-blue-900/50'
                                    "
                                >
                                    {{ record.method || 'POST' }}
                                </span>
                            </td>
                            <td class="px-3 md:px-6 py-3 md:py-4 text-zinc-400">
                                {{ record.operation_type || '-' }}
                            </td>
                            <td class="px-3 md:px-6 py-3 md:py-4 text-white">
                                {{ record.model }}
                            </td>
                            <td class="px-3 md:px-6 py-3 md:py-4">
                                <!-- 成功状态 -->
                                <span
                                    v-if="record.status === 'succeed'"
                                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20"
                                >
                                    <svg
                                        class="w-3 h-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                    {{ t('api.success') }}
                                </span>
                                <!-- 处理中状态 -->
                                <span
                                    v-else-if="record.status === 'processing'"
                                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                >
                                    <svg
                                        class="w-3 h-3 animate-spin"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path
                                            d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"
                                        />
                                        <path d="M21 3v5h-5" />
                                        <path
                                            d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"
                                        />
                                        <path d="M8 16H3v5" />
                                    </svg>
                                    {{ t('api.processing') }}
                                </span>
                                <!-- 失败状态 -->
                                <span
                                    v-else-if="record.status === 'failed'"
                                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20"
                                >
                                    <svg
                                        class="w-3 h-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" x2="12" y1="8" y2="12" />
                                        <line
                                            x1="12"
                                            x2="12.01"
                                            y1="16"
                                            y2="16"
                                        />
                                    </svg>
                                    {{ t('api.failed') }}
                                </span>
                            </td>
                            <td class="px-6 py-4 text-zinc-400">
                                {{ record.latency || '-' }}
                            </td>
                            <td
                                class="px-6 py-4 text-white font-medium text-right"
                            >
                                {{ record.cost > 0 ? record.cost : '-' }}
                            </td>
                        </tr>

                        <!-- 空状态 -->
                        <tr v-if="!loading && filteredRecords.length === 0">
                            <td
                                colspan="9"
                                class="px-6 py-12 text-center text-zinc-500"
                            >
                                <div class="flex flex-col items-center gap-3">
                                    <svg
                                        class="w-12 h-12 opacity-30"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path
                                            d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
                                        />
                                        <path d="M3 3v5h5" />
                                        <path d="M12 7v5l4 2" />
                                    </svg>
                                    <p>{{ t('api.noRecords') }}</p>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 分页 - 移动端固定底部，PC端正常布局 -->
        <div v-if="total > 0" class="pagination-container">
            <div class="pagination-text">
                {{ t('api.paginationText', {
                    from: (currentPage - 1) * pageSize + 1,
                    to: Math.min(currentPage * pageSize, total),
                    total: total
                }) }}
            </div>
            <el-pagination
                v-model:current-page="currentPage"
                :page-size="pageSize"
                :total="total"
                layout="prev, pager, next"
                :hide-on-single-page="false"
                class="custom-pagination"
                @current-change="handlePageChange"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    computed,
    onMounted,
    onBeforeUnmount,
    onUnmounted,
    watch,
} from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '#imports'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import {
    getGenerationRecordsApi,
    type HistoryRecord,
    type HistoryRecordStatus,
} from '~/api/api'
import { formatTimestamp, formatElapsedTime } from '~/utils/common'
import { useI18n } from '~/composables/useI18n'

const route = useRoute()
const { t } = useI18n()

// 使用多语言 SEO
const { canonicalUrl } = useMultilingualSeo({
    pageType: 'apiHistory',
    robots: 'noindex, follow',
})

useHead({
    script: [
        // BreadcrumbList Schema
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'WeryAI',
                            item: 'https://www.weryai.com',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('api.historyTitle'),
                            item: canonicalUrl.value,
                        },
                    ],
                })
            ),
        },
    ],
})

// ========== 数据状态 ==========
const historyRecords = ref<HistoryRecord[]>([])
const loading = ref(false)
const pollingTimer = ref<NodeJS.Timeout | null>(null)

// ========== 搜索和筛选 ==========
const searchQuery = ref('')
const showFilter = ref(false)
const filterStatus = ref<string>('all')
const filterRef = ref<HTMLElement | null>(null)

// ========== 分页 ==========
const currentPage = ref(1)
const pageSize = ref(20)
const total = ref(0)
/**
 * 游标分页 - 保存服务端返回的 page_cursor
 *
 * 工作原理：
 * 1. 首次请求或重置时，不携带 page_cursor，服务端返回第一页数据和新的 cursor
 * 2. 下一页请求时，携带上次返回的 cursor，服务端基于 cursor 返回下一页数据和新 cursor
 * 3. 搜索/筛选时清空 cursor，重新开始分页流程
 */
const pageCursor = ref<string>('')

const statusOptions = computed(() => [
    { value: 'all', label: t('api.allStatus') },
    { value: 'succeed', label: t('api.onlySuccess') },
    { value: 'failed', label: t('api.onlyFailed') },
    { value: 'processing', label: t('api.processing') },
])

// ========== 方法 ==========

/**
 * 获取历史记录数据
 * @param silent - 是否静默更新（轮询时使用，不显示 loading 状态）
 * @param resetCursor - 是否重置游标（搜索、筛选时需要重置）
 */
const fetchHistoryRecords = async (silent = false, resetCursor = false) => {
    try {
        // 在开始新的请求前，先停止已有的轮询，避免多个轮询同时运行
        if (!silent) {
            stopPolling()
            loading.value = true
        }

        // 如果需要重置游标，清空保存的 cursor
        if (resetCursor) {
            pageCursor.value = ''
        }

        const params: any = {
            page: currentPage.value,
            size: pageSize.value,
        }

        // 如果有 page_cursor，携带上次的游标
        if (pageCursor.value) {
            params.page_cursor = pageCursor.value
        }

        // 添加ID搜索
        if (searchQuery.value.trim()) {
            params.id = searchQuery.value.trim()
        }

        // 添加状态筛选 - 只有非"全部"时才传递status参数
        if (filterStatus.value !== 'all') {
            params.status = filterStatus.value as HistoryRecordStatus
        }

        const response = await getGenerationRecordsApi(params)
        if (response.status !== 200) return

        // 保存数据
        historyRecords.value = response.data.content || []
        total.value = response.data.total || 0

        // 保存服务端返回的 page_cursor，用于下次请求
        if (response.data.page_cursor) {
            pageCursor.value = response.data.page_cursor
            console.log('保存 page_cursor:', response.data.page_cursor)
        } else {
            // 如果服务端没有返回 cursor，清空本地保存的
            pageCursor.value = ''
        }

        // 检查是否有进行中的记录，如果有则开启轮询
        checkAndStartPolling()
    } catch (error) {
        console.error('获取历史记录失败:', error)
        historyRecords.value = []
        total.value = 0
        pageCursor.value = ''
    } finally {
        // 只有非静默模式才重置 loading
        if (!silent) {
            loading.value = false
        }
    }
}

/**
 * 搜索处理（回车触发）
 */
const handleSearch = () => {
    currentPage.value = 1
    // 搜索时重置游标，重新开始分页
    fetchHistoryRecords(false, true)
}

/**
 * 切换筛选
 */
const toggleFilter = () => {
    showFilter.value = !showFilter.value
}

/**
 * 选择状态筛选
 */
const selectStatus = (value: string) => {
    filterStatus.value = value
    showFilter.value = false
    currentPage.value = 1
    // 筛选时重置游标，重新开始分页
    fetchHistoryRecords(false, true)
}

/**
 * 处理分页变化
 * @param page - 新的页码
 */
const handlePageChange = (page: number) => {
    // 如果跳回第一页，重置游标；否则使用游标继续加载
    const shouldResetCursor = page === 1
    fetchHistoryRecords(false, shouldResetCursor)
}

/**
 * 检查是否有进行中的记录，并启动轮询
 */
const checkAndStartPolling = () => {
    // 先清除已有的轮询
    stopPolling()

    // 检查是否有进行中的记录
    const hasProcessing = historyRecords.value.some(
        record => record.status === 'processing'
    )

    if (hasProcessing) {
        // 开启轮询，每5秒静默刷新一次
        pollingTimer.value = setInterval(() => {
            fetchHistoryRecords(true) // 静默更新，不显示 loading
        }, 5000)
    }
}

/**
 * 停止轮询
 */
const stopPolling = () => {
    if (pollingTimer.value) {
        clearInterval(pollingTimer.value)
        pollingTimer.value = null
    }
}

// 点击外部关闭筛选菜单
const handleClickOutside = (event: MouseEvent) => {
    if (
        showFilter.value &&
        filterRef.value &&
        !filterRef.value.contains(event.target as Node)
    ) {
        showFilter.value = false
    }
}

// ========== 计算属性 ==========

/**
 * 展示用的记录列表（添加格式化字段）
 */
const displayRecords = computed(() => {
    return historyRecords.value.map(record => ({
        ...record,
        created_at: formatTimestamp(record.create_time),
        cost: record.credits,
        latency: formatElapsedTime(record.elapsed_time),
    }))
})

const filteredRecords = computed(() => displayRecords.value)

// ========== 生命周期 ==========

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    // 初始加载数据，重置游标
    fetchHistoryRecords(false, true)
})

// 组件卸载前清理轮询（更早触发，确保清理）
onBeforeUnmount(() => {
    stopPolling()
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    // 再次确保清除轮询
    stopPolling()
})

// 监听路由变化，切换路由时立即停止轮询
watch(
    () => route.path,
    (newPath, oldPath) => {
        // 如果路由变化了（离开当前页面），立即停止轮询
        if (newPath !== oldPath && oldPath?.includes('/api/history')) {
            stopPolling()
        }
    }
)
</script>

<style scoped lang="less">
.history-page {
    animation: fadeIn 0.3s ease-out;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fadeIn {
    animation: fadeIn 0.2s ease-out;
}

// 骨架屏流光动画
.skeleton-shimmer {
    background: linear-gradient(
        90deg,
        rgba(39, 39, 42, 0.4) 0%,
        rgba(63, 63, 70, 0.4) 50%,
        rgba(39, 39, 42, 0.4) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

/* 去除输入框默认选中样式 */
input[type='text'] {
    &:focus {
        outline: none !important;
        box-shadow: none !important;
        border-color: #27272a !important;
    }

    &:focus-visible {
        outline: none !important;
        box-shadow: none !important;
    }

    // 去除自动填充的默认样式
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 1000px #121212 inset !important;
        -webkit-text-fill-color: #ffffff !important;
        border-color: #27272a !important;
    }
}

/* 分页容器样式 */
.pagination-container {
    // 移动端：固定在屏幕底部
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 14px;
    background: #000000;
    z-index: 30;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    // PC端：正常布局
    @media (min-width: 768px) {
        position: static;
        margin-top: 20px;
        padding: 14px 7px;
        border-top: none;
        border-radius: 10.5px;
        flex-direction: row;
        justify-content: space-between;
        gap: 0;
    }
}

.pagination-text {
    text-align: center;
    color: #71717a;
    font-size: 10px;
    order: 1;

    @media (min-width: 768px) {
        text-align: left;
        font-size: 12px;
        order: 1;
    }
}

.custom-pagination {
    order: 2;

    @media (min-width: 768px) {
        order: 2;
    }
}

/* Element Plus 分页样式覆盖 */
.custom-pagination {
    :deep(.el-pagination) {
        display: flex;
        align-items: center;
        justify-content: center;

        @media (min-width: 768px) {
            justify-content: flex-end;
        }
    }

    // 按钮通用样式 - 移动端 37x31.5，间距 5px
    :deep(.btn-prev),
    :deep(.btn-next),
    :deep(.el-pager li) {
        min-width: 37px !important;
        width: 37px !important;
        height: 31.5px !important;
        padding: 0 !important;
        margin: 0 5px 0 0 !important;
        background: rgba(18, 18, 18, 1) !important;
        border: 1px solid #27272a !important;
        border-radius: 7px !important;
        color: #a1a1aa !important;
        font-size: 12px !important;
        font-weight: 500 !important;
        line-height: 1 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.3s !important;

        @media (min-width: 768px) {
            min-width: 31.5px !important;
            width: auto !important;
            height: 31.5px !important;
            margin: 0 5.3px 0 0 !important;
            background: #121212 !important;
            font-size: 12.3px !important;
            line-height: 31.5px !important;
        }

        &:hover:not(.is-disabled):not(.disabled):not(.is-active) {
            border-color: #99f768 !important;
            color: #99f768 !important;

            @media (min-width: 768px) {
                background: #121212 !important;
            }
        }
    }

    // 最后一个按钮不需要右边距
    :deep(.btn-next) {
        margin-right: 0 !important;
    }

    // 禁用状态（50% 透明度）
    :deep(.btn-prev.is-disabled),
    :deep(.btn-prev:disabled),
    :deep(.btn-next.is-disabled),
    :deep(.btn-next:disabled) {
        opacity: 0.5 !important;
        cursor: not-allowed !important;
        background: rgba(18, 18, 18, 1) !important;
        border-color: #27272a !important;
        color: #a1a1aa !important;

        @media (min-width: 768px) {
            background: #121212 !important;
        }

        &:hover {
            background: rgba(18, 18, 18, 1) !important;
            border-color: #27272a !important;
            color: #a1a1aa !important;

            @media (min-width: 768px) {
                background: #121212 !important;
            }
        }
    }

    // 当前页高亮
    :deep(.el-pager li.is-active) {
        background: #99f768 !important;
        border-color: #99f768 !important;
        color: #000000 !important;
        font-weight: 500 !important;
    }

    // 省略号样式
    :deep(.el-pager li.more) {
        background: transparent !important;
        border: none !important;
        color: #52525b !important;
    }

    // 上一页/下一页箭头 - 确保完全居中
    :deep(.btn-prev),
    :deep(.btn-next) {
        // 确保内容垂直水平居中
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        svg {
            width: 12px !important;
            height: 12px !important;
            flex-shrink: 0 !important;
            display: block !important;
            margin: auto !important;

            @media (min-width: 768px) {
                width: 14px !important;
                height: 14px !important;
            }
        }
    }
}

// 给页面内容添加底部 padding，避免被固定的分页遮挡（仅移动端）
.history-page-container {
    @media (max-width: 767px) {
        padding-bottom: 120px !important;
    }
}
</style>
